/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useCallback, useContext } from "react";
import { Logger } from "cms/utils/log/logger";
import { ImageEditContext } from "cms/images/image-selector/context/image-edit-context";
import { Button } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "cms/icons";
import { useUploadFile } from "cms/images/hooks/use-upload-file";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { ImageUploadStatus } from "cms/images/image-selector/dialog-edit/panels/replace-img-upload-status";
import { FileDropArea } from "cms/images/image-selector/dialog-edit/panels/file-drop-area";
import { useAuth } from "cms/auth/hooks/use-auth";
import { getFileTypeName } from "cms/images/image-selector/image-helpers";
import { DEFAULT_UPLOAD_STATUS } from "cms/images/image-selector/image-selector-config";

const { faRepeat } = solid;

const ImageEditReplacePanel = () => {

    const [newFile, setNewFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isUploaded, setIsUploaded] = useState(false);
    const [uploadStatus, setUploadStatus] = useState(DEFAULT_UPLOAD_STATUS);
    const { token } = useAuth();
    const { uploadFile } = useUploadFile();

    const imageEditContext = useContext(ImageEditContext);
    const setIsReplacing = imageEditContext?.setIsReplacing;
    const selectedImage = imageEditContext?.selectedImage;
    const setImage = imageEditContext?.setImage;
    const setSelectedImage = imageEditContext?.setSelectedImage;
    const onSelectImage = imageEditContext?.onSelectImage;
    const setImageHandler = imageEditContext?.setImageHandler;
    const onReload = imageEditContext?.onReload;
    const setHasChanges = imageEditContext?.setHasChanges;
    
    const file_type_name = getFileTypeName(selectedImage.file_type);

    const onImageUploaded = async (replaced_image) => {
        try {
            const { guid } = selectedImage;
            const response = await SimpliCityAdminSDK.image.update(
                token, guid, replaced_image
            );
            Logger.debug(response);
            const purgeResponse = await SimpliCityAdminSDK.image.purgeAll(token);
            Logger.debug(purgeResponse);
            Logger.debug("Image Replaced", selectedImage);
            const updatedImage = {
                ...selectedImage,
                ...response,
                // url: `${response.url}?t=${Date.now()}`,
            };
            setSelectedImage(updatedImage);
            setNewFile(null);
            setIsUploaded(true);
            setIsUploading(false);
            setIsReplacing(false);
            setHasChanges(true);
            if(uploadStatus.status === "UPLOADED") {
                setImage(selectedImage);
                onSelectImage(selectedImage);
                setImageHandler(selectedImage);
                onReload(selectedImage);
            }
        } catch (error) {
            Logger.error(error);
        }
    };

    const handleUpload = useCallback(async () => {
        setIsUploading(true);
        const replacedFile = new File([newFile], selectedImage?.file_name, { type: selectedImage?.file_type });
        await uploadFile({
            image: selectedImage,
            file: replacedFile,
            file_name: selectedImage?.file_name,
            onImageUploaded,
            setUploadStatus
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [newFile]);

    const handleCancel = useCallback(() => {
        setIsReplacing(false);
        setNewFile(null);
    }, [setIsReplacing]);

    return (
        <div className={`replace-section ${isUploaded ? "uploading" : ""}`}>
            <h2 className="sectionHeader">Replace Image</h2>
            <p className="replace-confirmation">
                Select a replacement image with the same file type extension as the original 
                image.<br />
                It will be renamed to match the original image filename, and will overwrite the 
                original file.<br /><br/>
                <i>
                    <u>Note:</u> If you do not see the image updated right away after upload, 
                    you may need to wait up to 15 mins to see the change due to caching at 
                    the CDN and on browsers.
                </i>
            </p>
            <div className="file-type">
                <strong>Type: </strong>
                <span className="file-type-name">{file_type_name} ({selectedImage.file_ext})</span>
            </div>
            <FileDropArea
                newFile={newFile}
                setNewFile={setNewFile}
                setIsUploaded={setIsUploaded}
                selectedImage={selectedImage}
                fileType={selectedImage?.file_type}
            />
            {newFile && (
                <ImageUploadStatus
                    isUploading={isUploading}
                />
            )}
            <div className="replace-window-footer">
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleCancel}
                    className="cancel-btn"
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpload}
                    className="replace-btn"
                    startIcon={<FontAwesomeIcon icon={faRepeat} />}
                    disabled={!isUploaded || isUploading}
                >
                    Replace
                </Button>
            </div>
        </div>
    );
};

export { ImageEditReplacePanel };
